import React, {useState, useEffect, useRef} from 'react';
import {Flex, Spacer, FormLabel, Textarea, Stack, Box, Text, Button, useTheme} from '@chakra-ui/react';
import {useForm} from 'react-hook-form';
import {CustomInput, CustomCheckbox, SubmitButton, DatePicker} from '../../General/FormInputs';
import FlexCard from '../../General/FlexCard';
import {useClient} from '../../../views/Maintenance/ClientMaintenance';
import {Client} from '../../../service/Urls';
import {post, queryStringBuilder, getFile} from '../../../service/ApiService';
import ErrorHelper from '../../../service/ErrorHelper';
import BoxCard from '../../General/BoxCard';
import {Report} from '../../../service/Urls';
import {isAdmin} from '../../../helpers/localStorage';
import {useQueryClient} from 'react-query';
import RegionSelectNew from '../../Region/RegionSelectNew';
import {EmptyClient} from '../../../constants/client/defaults';

export default function Demographics() {
  const {client, isAdding, setSelectedClientId} = useClient();
  const queryClient = useQueryClient();
  const [readOnly, setReadOnly] = useState(true);
  const [toast, setToast] = useState('');
  const [isSalesMan, setIsSalesMan] = useState(true);
  const [pdfUrl, setPdfUrl] = useState([]);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const theme = useTheme();

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    formState: {errors},
  } = useForm();

  const [isCremat, isCemet] = watch(['clCremat', 'clCemet']);

  useEffect(() => {
    if (!isCremat) {
      setValue('clCrcode', '');
    }

    if (!isCemet) {
      setValue('clCecode', '');
    }
  }, [isCremat, isCemet]);

  useEffect(() => {
    if (isAdmin()) {
      setIsSalesMan(false);
    }
  }, []);

  // for when we get a report back
  useEffect(() => {
    if (pdfUrl.length > 0) linkRef.current?.click();
  }, [pdfUrl]);

  useEffect(() => {
    if (client) {
      reset({...EmptyClient, ...client});
    } else {
      reset({...EmptyClient});
    }
  }, [client]);

  useEffect(() => {
    if (client?.clId || isAdding) {
      setReadOnly(false);
    } else {
      setReadOnly(true);
    }
  }, [client, isAdding]);

  const onSubmit = async data => {
    if (isSalesMan) {
      setToast({response: {error: 1}, errorMessage: 'Invalid Permissions'});
      return;
    }

    if (isAdding) {
      const response = await post(Client.ADD, data);
      setToast({response: response, successMessage: 'Successfully added ' + data.clCname});

      if (response.isSuccess) {
        queryClient.invalidateQueries('client');
        setSelectedClientId(response.id);
      } else {
        setToast({response: response, errorMessage: response.error});
      }
    } else {
      const response = await post(Client.UPDATE + '/' + client?.id, data);
      setToast({response: response, successMessage: 'Successfully updated ' + data.clCname});

      if (response.isSuccess) {
        queryClient.invalidateQueries('client');
      } else {
        setToast({response: response, errorMessage: response.error});
      }
    }
  };

  const printReport = async fileName => {
    const params = {
      StartDate: '2001-01-01',
      EndDate: '2001-01-01',
      DefaultReport: fileName,
    };

    const url = queryStringBuilder(`${Report.DEFAULT_REPORT}${fileName}`, params);
    // const url = queryStringBuilder(SurveyPrints.reportbuttonFile, params);
    const response = await getFile(url);
    if (response.error) {
      setToast({response: {error: 1}, errorMessage: 'Error getting report: ' + response.error});
      return;
    }

    const fileURL = window.URL.createObjectURL(response);

    if (!response.error) {
      setPdfUrl(fileURL);
    }
  };

  const Row = props => {
    return (
      <Flex mx="30" my="2">
        <Box bg="black" px="3">
          <Text color={props.textColor}> {props.bracket} </Text>
        </Box>
        <Spacer />
        <CustomInput
          register={register}
          requireInt
          min="0"
          max="100"
          errors={errors}
          w="20"
          id={'clTpib' + props.num + 'l'}
          bg="yellow.500"
          isReadOnly={readOnly}
        />
        <Spacer />
        <CustomInput
          register={register}
          min="0"
          requireInt
          max="100"
          errors={errors}
          w="20"
          id={'clTpib' + props.num + 'u'}
          bg="yellow.500"
          isReadOnly={readOnly}
        />
      </Flex>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <a href={pdfUrl} target="_blank" rel="noopener noreferrer" ref={linkRef} style={{display: 'none'}} />
      <Flex flexDir={{base: 'column', lg: 'row'}}>
        <Box>
          <FlexCard>
            <Box w="80%">
              <Flex mt="4" mb={{base: '0', xl: '4'}} textAlign={'start'}>
                <CustomCheckbox control={control} id="isspanish" name="Spanish Surveys" isReadOnly={readOnly} />
                <CustomCheckbox control={control} id="issendemail" name="Send e-mails" isReadOnly={readOnly} />
                <CustomCheckbox control={control} id="issendtext" name="Send Texts" isReadOnly={readOnly} />
                <CustomCheckbox control={control} id="isprintsurvletr" name="Print Survey/Letter" isReadOnly={readOnly} />
              </Flex>

              <Flex
                mt="4"
                mb={{base: '0', xl: '4'}}
                textAlign={'start'}
                justifyContent="flex-start"
                backgroundColor={theme.colors.secondary[100]}
                borderRadius={5}
                paddingY={2}
                gap={5}
              >
                <Text fontWeight={'semibold'} marginLeft={5}>
                  Electronic Surveys:
                </Text>
                <Box>
                  <CustomCheckbox control={control} id="isElectronicFuneral" name="Funeral" isReadOnly={readOnly} />
                </Box>

                <Box>
                  <CustomCheckbox control={control} id="isElectronicCremation" name="Cremation" isReadOnly={readOnly} />
                </Box>
                <Box>
                  <CustomCheckbox control={control} id="isElectronicPreNeed" name="PreNeed" isReadOnly={readOnly} />
                </Box>
              </Flex>

              <Flex>
                <CustomInput id="clCname" isReadOnly={readOnly} name="Account Name" register={register} errors={errors} />
                <CustomInput id="clAdd1" isReadOnly={readOnly} name="Address" register={register} errors={errors} required />
                <CustomInput id="clAdd2" isReadOnly={readOnly} name="Address 2" register={register} errors={errors} />
              </Flex>
              <Flex>
                {/* <ZipCityState
                  setValue={setValue}
                  control={control}
                  register={register}
                  errors={errors}
                  values={{
                    zip: {id: 'clZip', value: client?.clZip},
                    city: {id: 'clCity', value: client?.clCity},
                    state: {id: 'clState', value: client?.clState},
                  }}
                  parentData={zcsP}
                  isReadOnly={readOnly}
                /> */}
                <CustomInput id="clZip" name="Zip" maxLength={10} isReadOnly={readOnly} register={register} errors={errors} required />
                <CustomInput id="clCity" name="City" isReadOnly={readOnly} register={register} errors={errors} />
                <CustomInput id="clState" name="State" maxLength={2} isReadOnly={readOnly} register={register} errors={errors} />
              </Flex>
            </Box>
            <Box mt="10">
              <CustomCheckbox isReadOnly={readOnly} control={control} id="clActive" name="Inactive" mt="-5" />
              <CustomInput isReadOnly={readOnly} id="clSname" name="Short Name" register={register} errors={errors} required />
              <CustomInput isReadOnly={readOnly} errors={errors} id="clRep" name="Representative" register={register} defaultValue={client?.clRep} />
              <CustomInput isReadOnly={readOnly} errors={errors} id="clTrainer" name="Trainer" defaultValue={client?.clTrainer} register={register} />
            </Box>
          </FlexCard>

          <FlexCard>
            <Box w="100%">
              <Flex flexDir={{base: 'column', lg: 'row'}}>
                <Box>
                  <CustomInput isReadOnly={readOnly} id="clContact" name="Contact" register={register} errors={errors} required />
                  <CustomInput isReadOnly={readOnly} id="clPhone" name="Phone" register={register} errors={errors} required />
                  <CustomInput isReadOnly={readOnly} id="clFax" name="Fax" register={register} errors={errors} />
                </Box>
                <Spacer />
                <Box>
                  <Flex>
                    <DatePicker isReadOnly={readOnly} id="clSince" name="Client Start Date" register={register} errors={errors} required />
                    <CustomInput
                      isReadOnly={readOnly}
                      id="clCode"
                      name="Client 3 Char Code"
                      maxLength="3"
                      register={register}
                      errors={errors}
                      required
                    />
                    <CustomInput
                      isReadOnly={readOnly}
                      id="clCrcode"
                      name="Char Code Cremation"
                      register={register}
                      errors={errors}
                      disabled={!isCremat}
                      required={isCremat}
                      maxLength="4"
                    />
                    <CustomInput
                      isReadOnly={readOnly}
                      id="clCecode"
                      name="Char Code Cemetery"
                      register={register}
                      errors={errors}
                      disabled={!isCemet}
                      required={isCemet}
                      maxLength="4"
                    />
                  </Flex>
                  <CustomInput isReadOnly={readOnly} id="clSoftwar" name="Software System" register={register} errors={errors} required />
                  <RegionSelectNew
                    isReadOnly={readOnly}
                    name="regionId"
                    control={control}
                    setCode={(code: number) => setValue('clRegion', code)}
                    required
                  />
                </Box>
              </Flex>

              <Flex>
                <Box w="50%">
                  <CustomInput
                    id="surveyNotifEmail1"
                    name="Survey Notification E-mail Address 1"
                    type="email"
                    register={register}
                    errors={errors}
                    isReadOnly={readOnly}
                  />
                </Box>
                <Box w="50%">
                  <CustomInput
                    id="surveyNotifEmail2"
                    name="Survey Notification E-mail Address 2"
                    type="email"
                    register={register}
                    errors={errors}
                    isReadOnly={readOnly}
                  />
                </Box>
              </Flex>
            </Box>
          </FlexCard>

          <FlexCard>
            <CustomInput id="clWebsite" name="Web Site" register={register} errors={errors} isReadOnly={readOnly} w="50%" />
            <CustomInput id="clSentby" name="Survey sent by" register={register} errors={errors} isReadOnly={readOnly} w="50%" />
          </FlexCard>

          <FlexCard>
            <Box w="100%">
              <FormLabel htmlFor="clComment" mb="-0.5" ml="1" fontSize="sm">
                Comment
              </FormLabel>
              <Textarea placeholder="Comment" id="clComment" {...register('clComment')} isReadOnly={readOnly} />
            </Box>
          </FlexCard>
          {!isSalesMan && <SubmitButton name="Save" />}
        </Box>

        {/** Stack of all the cards on the right hand side */}
        <Stack ml="2">
          <Box bgGradient={'linear(to-tr, blue.600, blue.200)'} boxShadow="2xl" rounded="lg" border="1px" p="2" px="30" mb="2">
            <Text fontWeight="bold" fontSize="3xl">
              Survey Types
            </Text>
            <Flex justify="space-evenly">
              <Flex flexDirection="column">
                <CustomCheckbox id="clFamily" name="Funeral" control={control} isReadOnly={readOnly} w="100%" />
                <CustomCheckbox id="clCremat" name="Cremation" control={control} isReadOnly={readOnly} w="100%" />
                <CustomCheckbox id="clCemet" name="Cemetery" control={control} isReadOnly={readOnly} w="100%" />
                <CustomCheckbox id="clMonu" name="Monument" control={control} isReadOnly={readOnly} w="100%" />
              </Flex>
              <Flex flexDirection="column">
                <CustomCheckbox id="clPreneed" name="PreNeed" control={control} isReadOnly={readOnly} w="100%" />
                <CustomCheckbox id="clPet" name="Pet Cremation" control={control} isReadOnly={readOnly} w="100%" />
                <CustomCheckbox id="clEvent" name="Event Planning" control={control} isReadOnly={readOnly} w="100%" />
              </Flex>
            </Flex>
          </Box>
          <Box bgGradient={'linear(to-tr, teal.600, teal.200)'} boxShadow="2xl" rounded="lg" border="1px" p="2">
            <Text fontWeight="bold" fontSize="3xl">
              Ranking Groups
            </Text>
            <Flex mx="30">
              <CustomInput register={register} maxLength="1" errors={errors} name="Code" id="clRankgrp" placeholder="Code" isReadOnly={readOnly} />
            </Flex>
            <Flex>
              <Spacer />

              <Flex flexDir="column" mt="2" mx="5" justify="left">
                <CustomCheckbox id="clExclude" name="Exclude from Ranking Reports" control={control} isReadOnly={readOnly} w="100%" />
                <CustomCheckbox id="clCanada" name="Canadian" control={control} isReadOnly={readOnly} w="100%" />
                <CustomCheckbox id="clPrecoa" name="Precoa Client" control={control} isReadOnly={readOnly} w="100%" />
                <CustomCheckbox id="clSupgrp" name="Suppress Group Totals on CFS Reports" control={control} isReadOnly={readOnly} w="100%" />
                <CustomCheckbox id="clSecarr" name="Uses Secondary Arrangers" control={control} isReadOnly={readOnly} w="100%" />
              </Flex>
              <Spacer />
              <Spacer />
              <Spacer />
            </Flex>
          </Box>
          <Box bgGradient={'linear(to-tr, yellow.600, yellow.200)'} boxShadow="2xl" rounded="lg" border="1px" p="2">
            <Text fontWeight="bold" fontSize="3xl">
              TPI Brackets for CFS Reports
            </Text>

            <Box>
              <Row bracket="Bracket 1" num="1" textColor="red.500" />
              <Row bracket="Bracket 2" num="2" textColor="yellow.500" />
              <Row bracket="Bracket 3" num="3" textColor="green.500" />
            </Box>
          </Box>
          <BoxCard>
            <Stack>
              <Button type="button" colorScheme="secondary" onClick={() => printReport('client_list_by_rep.rdlx')}>
                List by Rep
              </Button>
              <Button type="button" colorScheme="secondary" onClick={() => printReport('client_list_by_state.rdlx')}>
                List by State
              </Button>
              <Button type="button" colorScheme="secondary" onClick={() => printReport('client_list_by_group.rdlx')}>
                List by Group
              </Button>
            </Stack>
          </BoxCard>
          {/* <ListClientsBy /> */}
        </Stack>
      </Flex>
      <ErrorHelper toastResponse={toast} />
    </form>
  );
}
